/**
 * OSS 直传工具函数
 * 用于富文本编辑器和其他需要上传文件到 OSS 的场景
 */

import { getOssUploadUrl, getOssUrl } from '@/api/upload'

/**
 * 上传文件到 OSS（直传方式）
 * @param {File} file - 要上传的文件对象
 * @param {string} fileType - 文件类型，用于分类存储（默认：rich_text）
 * @param {string} apiType - API类型，可选值：'user'（普通用户，默认）或 'admin'（管理员专用）
 * @returns {Promise<{fileUrl: string, ossFilePath: string}>} 返回文件URL和OSS路径
 */
export async function uploadFileToOSS(file, fileType = 'rich_text', apiType = 'user') {
  try {
    console.log('🔍 [OSS Upload] 开始上传文件到 OSS')
    console.log('  - 文件名:', file.name)
    console.log('  - 文件大小:', file.size)
    console.log('  - 文件类型:', file.type)
    console.log('  - 存储类型:', fileType)
    console.log('  - API类型:', apiType)
    
    // 步骤1：获取OSS签名URL
    const urlFormData = new FormData()
    urlFormData.append('filename', file.name)
    urlFormData.append('file_type', fileType)
    
    console.log('  - 请求签名URL...')
    // 根据apiType选择不同的API端点
    const urlResponse = apiType === 'admin' ? await getOssUploadUrl(urlFormData) : await getOssUrl(urlFormData)
    
    if (!urlResponse || !urlResponse.data) {
      throw new Error('获取OSS签名URL失败：响应数据为空')
    }
    
    const { presigned_url, oss_file_path, file_url, content_type } = urlResponse.data.data
    console.log('  - 签名URL获取成功')
    console.log('  - OSS文件路径:', oss_file_path)
    console.log('  - 文件URL:', file_url)
    
    // 步骤2：读取文件内容并上传到OSS
    console.log('  - 读取文件内容...')
    const fileBuffer = await readFileAsBuffer(file)
    
    console.log('  - 上传文件到OSS...')
    const uploadResponse = await fetch(presigned_url, {
      method: 'PUT',
      body: fileBuffer,
      headers: {
        'Content-Type': content_type || file.type || 'application/octet-stream'
      },
      mode: 'cors',
      credentials: 'omit'
    })
    
    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text().catch(() => '')
      throw new Error(`OSS上传失败 (${uploadResponse.status} ${uploadResponse.statusText})${errorText ? ': ' + errorText : ''}`)
    }
    
    console.log('✅ [OSS Upload] 文件上传成功')
    console.log('  - 文件URL:', file_url)
    
    return {
      fileUrl: file_url,
      ossFilePath: oss_file_path,
      fileName: file.name,
      fileSize: file.size
    }
    
  } catch (error) {
    // 检查是否是降级场景（401/403/404/501/500），这些情况下不显示错误日志
    const isFallbackScenario = error.response && (
      error.response.status === 401 || // 未授权（token无效等）
      error.response.status === 403 || // 禁止访问（权限不足等）
      error.response.status === 404 || // 接口不存在
      error.response.status === 501 || // OSS不支持直传
      error.response.status === 500    // 服务器错误
    )
    
    if (!isFallbackScenario) {
      console.error('❌ [OSS Upload] 文件上传失败:', error)
    }
    
    // 如果是OSS不支持直传的错误，抛出特殊错误以便降级处理
    if (error.response && error.response.status === 501) {
      throw new Error('OSS_NOT_SUPPORTED')
    }
    
    throw error
  }
}

/**
 * 读取文件为 ArrayBuffer
 * @param {File} file - 文件对象
 * @returns {Promise<ArrayBuffer>} 文件的 ArrayBuffer
 */
function readFileAsBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target.result)
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

/**
 * 上传文件到 OSS（带降级处理）
 * 如果 OSS 不支持直传，会自动降级到传统上传方式
 * @param {File} file - 要上传的文件对象
 * @param {string} fileType - 文件类型，用于分类存储（默认：rich_text）
 * @param {Function} fallbackUpload - 降级上传函数（可选）
 * @param {string} apiType - API类型，可选值：'user'（普通用户，默认）或 'admin'（管理员专用）
 * @returns {Promise<{fileUrl: string}>} 返回文件URL
 */
export async function uploadFileToOSSWithFallback(file, fileType = 'rich_text', fallbackUpload = null, apiType = 'user') {
  try {
    return await uploadFileToOSS(file, fileType, apiType)
  } catch (error) {
    // 检查是否需要降级到传统上传
    // 1. 检查是否是OSS不支持直传的错误
    // 2. 检查是否是HTTP错误（401/403/404/501/500）
    // 3. 检查是否是CORS错误或网络错误
    const isCORSError = error.message && (error.message.includes('CORS') || error.message.includes('cross-origin') || error.message.includes('Failed to fetch'))
    const shouldFallback =
      error.message === 'OSS_NOT_SUPPORTED' || // OSS不支持直传
      (error.response && (
        error.response.status === 401 || // 未授权（token无效等）
        error.response.status === 403 || // 禁止访问（权限不足等）
        error.response.status === 501 || // OSS不支持直传
        error.response.status === 404 || // 接口不存在（本地开发环境常见）
        error.response.status === 500    // 服务器错误
      )) ||
      isCORSError // CORS错误或网络错误也降级到传统上传
    
    if (isCORSError) {
      console.log('ℹ️ [OSS Upload] 检测到CORS错误或网络错误，自动使用传统上传方式...')
    }
    
    if (shouldFallback) {
      // 静默降级，不显示错误信息（这是正常的降级流程）
      if (error.response && error.response.status === 401) {
        console.log('ℹ️ [OSS Upload] 认证失败（token无效），自动使用传统上传方式...')
      } else if (error.response && error.response.status === 403) {
        console.log('ℹ️ [OSS Upload] OSS接口权限不足（403），自动使用传统上传方式...')
      } else if (error.response && error.response.status === 404) {
        console.log('ℹ️ [OSS Upload] OSS接口不可用（本地开发环境），自动使用传统上传方式...')
      } else {
        console.log('⚠️ [OSS Upload] OSS不支持直传，使用降级上传方式...')
      }
      
      if (fallbackUpload) {
        return await fallbackUpload(file)
      } else {
        throw new Error('OSS不支持直传，且未提供降级上传函数')
      }
    }
    
    // 其他错误直接抛出
    throw error
  }
}



